import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { colors, constants, generateRoutePath } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { ExampleQuestionsCarousel } from "components/shared/ExampleQuestionsCarousel/ExampleQuestionsCarousel";
import { PrerequisiteTierSections } from "components/shared/PrerequisiteTierSections";
import { fetchConsensusQuestionsPage } from "utils/consensusQuestions";
import { useStandards } from "hooks/useStandards/useStandards";
import { getGradeLabel, sortStandardsByCode, isAssignableTarget } from "utils/standards";
import { AssignGroupsDistributionModal } from "../Educator/Dashboard/AssignGroupsDistributionModal";
import { ViewQuestionsModal } from "../Educator/Dashboard/ViewQuestionsModal";
import { QuestionFilterView } from "../LessonsStandards/QuestionFilterView";
import { QuestionFilterPanel } from "../LessonsStandards/QuestionFilterPanel";
import { EMPTY_QUESTION_FILTER, toTrackQuestionFilters, customizedTrackTypes } from "../LessonsStandards/questionFilters";
import { useStandardQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { useStandardScopedQuestions, useQuestionAvailability } from "hooks/useCurriculum/useCurriculum";
import "./groupCodeStandardSearch.css";
import pie_chart from "assets/pie-chart.svg";
import arrow_down from "assets/Arrow_Down.svg";
import arrow_right from "assets/Arrow_Right.svg";
import { useHostPageName, usePanelFirstView, useViewedPageTracking } from "hooks/usePageViewTracker";

// Flatten a relationships track (levels of standards) down to its codes.
const trackCodes = (groups) =>
  (groups || []).flatMap((g) => (g.standards || []).map((s) => s.code)).filter(Boolean);

const STANDARD_PANEL_NAME = "Standard Detail";
const STANDARD_TAB_LABELS = { core: "Core", prerequisites: "Prerequisites", extensions: "Extensions" };

export const StandardDetailView = ({ standard, groupCode, onStandardClick, prerequisiteOf, pickerMode = false, onQuestionFiltersChange, showZeroCountStandards = false }) => {
  const history = useHistory();
  const location = useLocation();

  // Get classId from URL to preserve class selection when returning to dashboard
  const urlParams = new URLSearchParams(location.search);
  const classIdFromUrl = urlParams.get("classId");
  // Phase 1 entry point: FE-2 routes here with intent=assign so we swap the
  // per-code Save button for the bulk FE-4 distribution modal.
  const intent = urlParams.get("intent");
  const classroomIdFromUrl = urlParams.get("classroomId");
  const totalAvailableFromUrl = Number(urlParams.get("totalAvailable")) || 0;
  // FE-7: pickerMode is set by the View & Change Standard page to render the
  // tabs without the per-code Save button or distribution modal — the parent
  // page owns the Save/Cancel footer and confirmation flow.
  const isAssignMode = intent === "assign" && classroomIdFromUrl && !pickerMode;
  // The non-assign footer's Save posts to `group/{groupCode}`, so it needs a
  // real group code attached — not the `_assign` / `search` routing sentinels.
  const SENTINEL_GROUP_CODES = ["_assign", "search"];
  const hasAttachedGroupCode = Boolean(groupCode) && !SENTINEL_GROUP_CODES.includes(groupCode);
  const [isDistributionModalOpen, setIsDistributionModalOpen] = useState(false);
  const [showExampleQuestions, setShowExampleQuestions] = useState(true);
  const [showPrerequisites, setShowPrerequisites] = useState(true);
  const [showStandardsList, setShowStandardsList] = useState(false);
  // FE-3: Core Standard / Prerequisites / Extensions tabs in assign-mode.
  const [activeTab, setActiveTab] = useState("core");
  const [questions, setQuestions] = useState([]);
  const [questionsTotal, setQuestionsTotal] = useState(0);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isLoadingMoreQuestions, setIsLoadingMoreQuestions] = useState(false);
  const [previousStandardCode, setPreviousStandardCode] = useState(null);
  // FE-8: single modal shared across all prereq/extension "View Questions" buttons.
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);
  // Inline accordion for prereq / extension "View Questions" — keyed by standard code.
  const [expandedItemCode, setExpandedItemCode] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [groupConfig, setGroupConfig] = useState(null);
  const standardsData = useStandards(true);

  // Advanced Selection (question filter) — per (track_type, standard_code), mirrors
  // LessonsStandards' filter state. Keyed `${trackType}|${code ?? ""}`;
  // Core is keyed `core|` (no code) since a group's Core track always resolves to the
  // selected standard itself. Only meaningful in assign/picker mode.
  const [filters, setFilters] = useState({});
  // Core tab only: the filter panel shows on arrival, same as the lesson flow —
  // the filtered carousel stays visible alongside it.
  const filterKeyOf = (trackType, code) => `${trackType}|${code || ""}`;
  const getFilter = (trackType, code) => filters[filterKeyOf(trackType, code)] || EMPTY_QUESTION_FILTER;
  const setFilter = (trackType, code, next) =>
    setFilters((prev) => ({ ...prev, [filterKeyOf(trackType, code)]: next }));

  useEffect(() => {
    onQuestionFiltersChange?.(filters);
  }, [filters, onQuestionFiltersChange]);

  // Determine entity type and get appropriate data
  const entityType = standard.type || "standard";
  const isStandard = entityType === "standard";
  const isDomain = entityType === "domain";
  const isCluster = entityType === "cluster";
  const isGrade = entityType === "grade";
  // Clusters and domains are Core-only targets but assignable; only whole grades
  // are not. Distinct from isStandard, which still gates the relationships fetch.
  const canAssign = isAssignableTarget(standard);
  // Zero mapped questions (the tree's crosswalk count is 0) still renders the
  // detail, but the question sections show the zero count plus a warning.
  // Assignment is NOT gated on this, the save/assign buttons stay enabled.
  // Only a state framework surfaces such a node (the Common Core tree hides
  // them), so the Common Core empty state is unchanged.
  const zeroMappedQuestions =
    showZeroCountStandards && canAssign && (standard.count === 0 || standard.challenge_count === 0);
  const zeroCountWarning = zeroMappedQuestions
    ? `0 questions are currently mapped to this ${entityType}. You can still assign it to a group.`
    : null;

  // Core tab's own facets + filtered questions (mirrors LessonsStandards/LessonDetailPanel's
  // parent-managed Core query, standard-scoped).
  // Inlines the showTabs condition (defined further down, for JSX gating) since hooks
  // can't be called after a conditionally-defined variable.
  const coreScopeActive = (isAssignMode || pickerMode) && isStandard;
  const coreFilter = getFilter("core", null);
  const coreQueryFilter = { learningComponents: coreFilter.learningComponents, questionType: coreFilter.questionType };
  const {
    facets: coreFacets,
    hasLoaded: coreFacetsLoaded,
    error: coreFacetsError,
  } = useStandardQuestionFacets(coreScopeActive ? standard.code : null, coreQueryFilter);
  const {
    questions: coreQuestions,
    total: coreQuestionsTotal,
    loading: coreQuestionsLoading,
    loadMore: loadMoreCoreQuestions,
  } = useStandardScopedQuestions(coreScopeActive ? standard.code : null, coreQueryFilter);

  // Get display values based on entity type
  const getEntityCode = () => {
    if (isStandard) return standard.code;
    if (isDomain || isCluster) return standard.code;
    if (isGrade) return standard.grade;
    return "";
  };

  const getEntityName = () => {
    if (isStandard) return standard.name;
    if (isDomain || isCluster) return standard.name;
    if (isGrade) return getGradeLabel(standard.grade);
    return "";
  };

  const getEntityTypeBadge = () => {
    if (isStandard) return "STANDARD";
    if (isDomain) return "DOMAIN";
    if (isCluster) return "CLUSTER";
    if (isGrade) return "GRADE";
    return "STANDARD";
  };

  // Fetch group config to get current session length
  useEffect(() => {
    if (pickerMode) return;
    const fetchGroupConfig = async () => {
      try {
        const data = await request(`group/${groupCode}`, {
          method: "GET",
        });
        setGroupConfig(data.config);
      } catch (e) {
        console.error("Error fetching group config:", e);
        logError("Failed to fetch group config", e, {
          category: LogCategory.ERROR,
          groupCode,
        });
      }
    };

    // The routing sentinels (`_assign` / `search`) are not group codes — asking
    // the API for them is a guaranteed 404, and `groupConfig` is only rendered
    // behind this same check anyway.
    if (hasAttachedGroupCode) {
      fetchGroupConfig();
    }
  }, [groupCode, hasAttachedGroupCode, pickerMode]);

  // Collapse sections and reset when standard changes
  useEffect(() => {
    const currentCode = getEntityCode();
    if (previousStandardCode !== null && previousStandardCode !== currentCode) {
      setShowExampleQuestions(true);
      setShowPrerequisites(true);

      // Standards list should be collapsed by default on each node details view
      setShowStandardsList(false);

      setQuestions([]);
      setQuestionsTotal(0);
      setIsLoadingQuestions(false);
      setIsLoadingMoreQuestions(false);
      // Unlike questionsRequestIdRef (implicitly bumped by the fetchQuestions()
      // call below), nothing here calls loadMoreQuestions, so its lineage needs
      // an explicit bump to invalidate any load-more still in flight for the
      // old standard, otherwise its stale page can land on the new standard.
      loadMoreRequestIdRef.current += 1;
      setActiveTab("core");
      setFilters({});
      // Fetch questions for the new standard if example questions section is/will be shown
      fetchQuestions();
    }
    setPreviousStandardCode(currentCode);
  }, [standard]);

  // FE-3: fetch BE-1 relationships when a standard is picked in assign-mode.
  // FE-7: also fetch in pickerMode so the change-standard page renders tabs.
  // Backed by React Query so re-selecting a code while its own relationships
  // request is still in flight (e.g. navigating away and back before the
  // backend responds) dedupes onto the one in-flight request for that code,
  // instead of firing a new physical GET each time (OKO2-3534).
  const relationshipsEnabled = (isAssignMode || pickerMode) && isStandard && Boolean(standard.code);
  const { data: relationshipsData, isInitialLoading: isLoadingRelationships } = useQuery({
    queryKey: ["standards", standard.code, "relationships"],
    queryFn: () => request(`standards/${standard.code}/relationships`, { method: "GET" }),
    enabled: relationshipsEnabled,
    onError: (e) => {
      logError("Failed to fetch standard relationships", e, {
        category: LogCategory.ERROR,
        standardCode: standard.code,
      });
    },
  });
  const relationships = relationshipsEnabled ? relationshipsData ?? null : null;

  // Fetch questions when example questions section is toggled
  useEffect(() => {
    if (showExampleQuestions && questions.length === 0) {
      fetchQuestions();
    }
  }, [showExampleQuestions]);

  // Determine the standard_prefix to page questions by, based on entity type.
  const getQuestionPrefix = () => {
    if (entityType === "grade") return standard.grade;
    // standard / cluster / domain all page by their code.
    return standard.code;
  };

  // Guards the legacy example-questions carousel's fetch/loadMore against the
  // OKO2-3534 request-fan-out failure mode: `questionsInFlightRef` dedupes a
  // re-entrant fetchQuestions() call for a prefix that already has one in flight
  // (e.g. toggling the section closed then open again before the first resolves).
  // `fetchQuestions` and `loadMoreQuestions` are independent request lineages
  // (initial load vs. pagination), so each gets its own monotonic counter —
  // `questionsRequestIdRef` for fetchQuestions, `loadMoreRequestIdRef` for
  // loadMoreQuestions — so that overlapping fetches within one lineage resolving
  // out of order only ever get invalidated by a newer request from that same
  // lineage, never by the other.
  const questionsInFlightRef = useRef(new Map());
  const questionsRequestIdRef = useRef(0);
  const loadMoreRequestIdRef = useRef(0);

  const fetchQuestions = useCallback(async () => {
    const prefix = getQuestionPrefix();
    let inFlight = questionsInFlightRef.current.get(prefix);
    if (!inFlight) {
      inFlight = fetchConsensusQuestionsPage({ prefix }).finally(() => {
        questionsInFlightRef.current.delete(prefix);
      });
      questionsInFlightRef.current.set(prefix, inFlight);
    }
    const myRequestId = ++questionsRequestIdRef.current;
    setIsLoadingQuestions(true);
    try {
      const { questions: pageQuestions, total } = await inFlight;
      if (questionsRequestIdRef.current === myRequestId) {
        setQuestions(pageQuestions);
        setQuestionsTotal(total);
      }
    } catch (e) {
      console.error("Error fetching questions:", e);
      logError("Failed to fetch standard questions", e, {
        category: LogCategory.ERROR,
      });
      if (questionsRequestIdRef.current === myRequestId) {
        setQuestions([]);
        setQuestionsTotal(0);
      }
    } finally {
      if (questionsRequestIdRef.current === myRequestId) {
        setIsLoadingQuestions(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standard.grade, standard.code, entityType]);

  // Append the next page of questions once the educator reaches the end of the
  // loaded set. Returns the newly fetched questions (empty on no-op / failure).
  const loadMoreQuestions = async () => {
    if (isLoadingMoreQuestions || questions.length >= questionsTotal) return [];
    const myRequestId = ++loadMoreRequestIdRef.current;
    setIsLoadingMoreQuestions(true);
    try {
      const { questions: pageQuestions, total } = await fetchConsensusQuestionsPage({
        prefix: getQuestionPrefix(),
        offset: questions.length,
      });
      if (loadMoreRequestIdRef.current === myRequestId) {
        setQuestions((prev) => [...prev, ...pageQuestions]);
        setQuestionsTotal(total);
      }
      return pageQuestions;
    } catch (e) {
      logError("Failed to fetch more standard questions", e, {
        category: LogCategory.ERROR,
      });
      return [];
    } finally {
      if (loadMoreRequestIdRef.current === myRequestId) {
        setIsLoadingMoreQuestions(false);
      }
    }
  };

  const openQuestionsModal = (item) => {
    setViewQuestionsTarget({
      code: item.code,
      name: item.name,
      description: item.description,
    });
  };

  const toggleItem = (code) => {
    setExpandedItemCode((prev) => (prev === code ? null : code));
  };

  // Make a clickable div behave like a keyboard-operable button: Enter / Space
  // fire the same action as a click. Used for the section toggles and the
  // clickable standard rows in the detail panel so keyboard users can operate
  // them after selecting a standard from the navigation tree.
  const onActivateKeyDown = (action) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  const findStandardByCode = (code) => {
    for (const gradeLevel of standardsData) {
      if (gradeLevel.domains) {
        for (const domain of gradeLevel.domains) {
          if (domain.clusters) {
            for (const cluster of domain.clusters) {
              if (cluster.standards) {
                for (const std of cluster.standards) {
                  if (std.code === code) {
                    return std;
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const handleUpdateGroupCode = async () => {
    // Prevent saving if a grade is selected (only allow domain and below)
    if (entityType === 'grade') {
      setError("Please select a specific domain within the grade. You cannot select an entire grade.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let standardPrefix;
      if (entityType === 'domain' || entityType === 'cluster' || entityType === 'standard') {
        standardPrefix = standard.code;
      }
      const sessionLength = groupConfig?.session_length || 15;
      await request(`group/${groupCode}`, {
        payload: {
          config: {
            activity_type: constants.CONSENSUS_ENUM,
            classroom_id: groupConfig?.classroom_id,
            standard_prefix: standardPrefix,
            session_length: sessionLength,
          }
        }
      });
      setIsSaving(false);
      const dashboardPath = generateRoutePath(constants.EDUCATOR_ROUTE_NAME);
      history.push(classIdFromUrl ? `${dashboardPath}?classId=${classIdFromUrl}` : dashboardPath);
    } catch (e) {
      setIsSaving(false);
      setError(e?.detail || e?.message || "An unknown error occurred.");
      logError("Failed to save standard configuration", e, {
        category: LogCategory.ERROR,
        groupCode,
      });
    }
  };

  const handleCancel = () => {
    const dashboardPath = generateRoutePath(constants.EDUCATOR_ROUTE_NAME);
    history.push(classIdFromUrl ? `${dashboardPath}?classId=${classIdFromUrl}` : dashboardPath);
  };


  // Get standards list for domain or cluster
  const getStandardsList = () => {
    if (isDomain && standard.clusters) {
      // For domains, show all standards from all clusters
      const allStandards = [];
      for (const cluster of standard.clusters) {
        if (cluster.standards) {
          // Filter out standards with challenge_count of 0
          const filteredStandards = cluster.standards.filter(std =>
            std.count !== 0 && std.challenge_count !== 0
          );
          allStandards.push(...filteredStandards);
        }
      }
      return allStandards;
    } else if (isCluster && standard.standards) {
      // For clusters, show standards directly, filtering out those with challenge_count of 0
      return standard.standards.filter(std =>
        std.count !== 0 && std.challenge_count !== 0
      );
    }
    return [];
  };

  // Get prerequisites for the current standard
  const getPrerequisites = () => {
    if (isStandard && standard.prerequisites && standard.prerequisites.length > 0) {
      return standard.prerequisites.map(prereqCode => {
        const prereqStandard = findStandardByCode(prereqCode);
        return prereqStandard ? { ...prereqStandard, code: prereqCode } : { code: prereqCode };
      });
    }
    return [];
  };

  const standardsList = getStandardsList();
  const prerequisites = getPrerequisites();

  // FE-3: Tabs data — derived from BE-1 relationships response. A track also needs
  // at least one question-bearing standard, else there is nothing to assign; the
  // relationships payload's has_questions is not used, since availability must come
  // from the same question call the cards themselves make.
  const prereqCodes = useMemo(() => trackCodes(relationships?.prerequisites), [relationships]);
  const extCodes = useMemo(() => trackCodes(relationships?.extensions), [relationships]);
  const availabilityCodes = useMemo(
    () => [...new Set([...prereqCodes, ...extCodes])],
    [prereqCodes, extCodes],
  );
  const { availability, resolved: availabilityResolved } = useQuestionAvailability({
    flow: "standard",
    codes: availabilityCodes,
  });
  // Enabled while probing, so tabs don't flicker; the effect below moves off a tab
  // that turns out to be empty.
  const trackAssignable = (codes) =>
    codes.length > 0 && (!availabilityResolved || codes.some((c) => availability[c]));
  const hasPrerequisites = trackAssignable(prereqCodes);
  const hasExtensions = trackAssignable(extCodes);

  useEffect(() => {
    if (activeTab === "prerequisites" && !hasPrerequisites) setActiveTab("core");
    if (activeTab === "extensions" && !hasExtensions) setActiveTab("core");
  }, [activeTab, hasPrerequisites, hasExtensions]);
  const showTabs = (isAssignMode || pickerMode) && isStandard;

  const hostPageName = useHostPageName();
  const { trackView } = useViewedPageTracking();
  const trackTabView = (key) =>
    trackView(hostPageName, {
      panel: STANDARD_PANEL_NAME,
      standard_code: standard.code,
      tab: STANDARD_TAB_LABELS[key],
    });
  // A new standard opens on Core.
  usePanelFirstView(showTabs ? standard.code : null, () => trackTabView("core"));
  const selectTab = (key) => {
    if (key !== activeTab) trackTabView(key);
    setActiveTab(key);
  };

  const renderRelatedStandardCard = (item, index, isLast, trackType) => {
    const code = item.code;
    const isExpanded = expandedItemCode === code;

    return (
      <div
        key={code || index}
        style={{
          borderBottom: isLast ? "none" : "1px solid #D5D3CE",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          padding: 24,
        }}
      >
        <div style={{
          color: "#BA4A21",
          fontFamily: "'ABC Marfa', 'Inter', sans-serif",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.5px",
          lineHeight: "18px",
          textTransform: "uppercase",
        }}>
          STANDARD - {item.code}
        </div>
        <div
          style={{
            color: "#002E19",
            cursor: onStandardClick ? "pointer" : "default",
            fontFamily: "'Inter', sans-serif",
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "0.25px",
            lineHeight: "22px",
          }}
          role={onStandardClick ? "button" : undefined}
          tabIndex={onStandardClick ? 0 : undefined}
          aria-label={onStandardClick ? `Open standard ${item.code}` : undefined}
          onClick={() => onStandardClick && onStandardClick(item, standard.grade, standard.domain, standard.cluster, standard)}
          onKeyDown={
            onStandardClick
              ? onActivateKeyDown(() => onStandardClick(item, standard.grade, standard.domain, standard.cluster, standard))
              : undefined
          }
        >
          {item.name || "Loading..."}
        </div>
        {item.description && (
          <div style={{
            color: "#002E19",
            fontFamily: "'Inter', sans-serif",
            fontSize: 16,
            fontWeight: 400,
            letterSpacing: "0.25px",
            lineHeight: "22px",
          }}>
            {item.description}
          </div>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleItem(code);
          }}
          style={{
            alignItems: "center",
            alignSelf: "flex-start",
            background: "transparent",
            border: "none",
            color: "#002E19",
            cursor: "pointer",
            display: "inline-flex",
            fontFamily: "'Inter', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            gap: 8,
            lineHeight: "20px",
            padding: "4px 0",
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            style={{ transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
          >
            <path d="M3 1L7 5L3 9" stroke="#002E19" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          View Questions
        </button>
        {isExpanded && (
          <div style={{ marginTop: 16 }}>
            <QuestionFilterView
              standardCode={code}
              filter={getFilter(trackType, code)}
              onFilterChange={(next) => setFilter(trackType, code, next)}
              showStandards={false}
              showAutoAssign
            />
          </div>
        )}
      </div>
    );
  };

  const renderLevelGroups = (groups, header, trackType) => {
    // Level 2/3 are intentionally hidden — only level-1 extensions are surfaced here.
    const level1 = (groups || []).find((g) => g.level === 1);
    if (!level1 || !level1.standards || level1.standards.length === 0) return null;
    const items = sortStandardsByCode(level1.standards);
    return (
      <div key="level-1" className="level-group" style={{ background: "transparent" }}>
        {header}
        <div className="level-group-cards" style={{ background: "transparent", border: "none", borderRadius: 0 }}>
          {items.map((s, i) => renderRelatedStandardCard(s, i, i === items.length - 1, trackType))}
        </div>
      </div>
    );
  };

  const renderPrerequisiteSections = (groups) => (
    <PrerequisiteTierSections
      groups={groups}
      entityCode={getEntityCode()}
      renderCard={(s, i, last) => renderRelatedStandardCard(s, i, last, "prerequisites")}
    />
  );

  return (
    <div className="standard-detail-view-single">
      {/* Breadcrumb for Prerequisite Navigation */}
      {prerequisiteOf && (
        <div className="search-results-header" style={{ marginBottom: "24px" }}>
          <button
            className="back-to-tree-button"
            onClick={() => {
              history.goBack();
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Prerequisite of {prerequisiteOf.code}
          </button>
        </div>
      )}

      {/* Entity Header */}
      <div className="standard-header">
        <div className="standard-badge-container">
          <img src={pie_chart} alt="pie chart" />
          <div className="standard-type-badge">
            <span className="standard-code-large">{getEntityTypeBadge()} - {getEntityCode()}</span>
            <span className="standard-title">{getEntityName()}</span>
          </div>
        </div>
        {standard.description && (
          <p className="standard-description">{standard.description}</p>
        )}
      </div>

      {/* Grade Selection Warning */}
      {isGrade && (
        <Alert variant="info" style={{ marginTop: "16px" }}>
          <strong>Note:</strong> You cannot select an entire grade for your group. Please select a specific domain from the left navigation to continue.
        </Alert>
      )}

      {/* Standards List for Domain or Cluster */}
      {(isDomain || isCluster) && standardsList.length > 0 && (
        <Card className="detail-section">
          <Card.Header
            className="section-header"
            role="button"
            tabIndex={0}
            aria-expanded={showStandardsList}
            onClick={() => setShowStandardsList(!showStandardsList)}
            onKeyDown={onActivateKeyDown(() => setShowStandardsList(!showStandardsList))}
            style={{ cursor: "pointer" }}
          >
            <div className="section-header-content">
              <img src={showStandardsList ? arrow_down : arrow_right} alt="arrow" style={{ width: "16px" }} />
              <span className="section-title">Standards for {getEntityCode()}</span>
            </div>
          </Card.Header>
          {showStandardsList && (
            <Card.Body style={{ padding: "16px", backgroundColor: colors.milkGlass500 }}>
              <ul className="standards-list">
                {standardsList.map((std, index) => (
                  <li key={std.code || index} className="standards-list-item">
                    <button
                      type="button"
                      className="standards-list-item-button"
                      onClick={() => onStandardClick && onStandardClick(std, standard.grade, standard.domain, standard.cluster)}
                    >
                      <strong>{std.code}</strong> - {std.name}
                    </button>
                  </li>
                ))}
              </ul>
            </Card.Body>
          )}
        </Card>
      )}

      {/* FE-3: Tabs row — assign-mode standard picker only. */}
      {showTabs && (
        <>
          <div className="detail-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === "core"}
              className={`detail-tab${activeTab === "core" ? " active" : ""}`}
              onClick={() => selectTab("core")}
            >
              Core
            </button>
            <span className="detail-tab-wrapper">
              <button
                role="tab"
                aria-selected={activeTab === "prerequisites"}
                className={`detail-tab${activeTab === "prerequisites" ? " active" : ""}`}
                onClick={() => hasPrerequisites && selectTab("prerequisites")}
                disabled={!hasPrerequisites}
              >
                Prerequisites
              </button>
              {!hasPrerequisites && (
                <span className="detail-tab-tooltip" role="tooltip">
                  No prerequisites available for this standard.
                  <svg className="detail-tab-tooltip-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                    <path d="M0 0V12L16 0H0Z" fill="#EAEAFC"/>
                  </svg>
                </span>
              )}
            </span>
            <span className="detail-tab-wrapper">
              <button
                role="tab"
                aria-selected={activeTab === "extensions"}
                className={`detail-tab${activeTab === "extensions" ? " active" : ""}`}
                onClick={() => hasExtensions && selectTab("extensions")}
                disabled={!hasExtensions}
              >
                Extensions
              </button>
              {!hasExtensions && (
                <span className="detail-tab-tooltip" role="tooltip">
                  No extensions available for this standard.
                  <svg className="detail-tab-tooltip-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                    <path d="M0 0V12L16 0H0Z" fill="#EAEAFC"/>
                  </svg>
                </span>
              )}
            </span>
          </div>
        </>
      )}

      {/* Example Questions — legacy accordion carousel, unfiltered (no tabs). */}
      {!showTabs && (
        <Card className="detail-section">
          <Card.Header
            className="section-header"
            role="button"
            tabIndex={0}
            aria-expanded={showExampleQuestions}
            onClick={() => setShowExampleQuestions(!showExampleQuestions)}
            onKeyDown={onActivateKeyDown(() => setShowExampleQuestions(!showExampleQuestions))}
            style={{ cursor: "pointer" }}
          >
            <div className="section-header-content">
              <img src={showExampleQuestions ? arrow_down : arrow_right} alt="arrow" style={{ width: "16px" }} />
              <span className="section-title">Example Questions</span>
            </div>
          </Card.Header>

          {showExampleQuestions && (
            <Card.Body style={{ padding: 0, backgroundColor: colors.milkGlass500 }}>
              {isLoadingQuestions ? (
                <div style={{ textAlign: "center", padding: "32px" }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p style={{ marginTop: "16px" }}>Loading questions...</p>
                </div>
              ) : questions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px" }}>
                  {zeroCountWarning || <>No questions available for this {entityType}.</>}
                </div>
              ) : (
                <ExampleQuestionsCarousel
                  questions={questions}
                  total={questionsTotal}
                  onLoadMore={loadMoreQuestions}
                />
              )}
            </Card.Body>
          )}
        </Card>
      )}

      {/* Core Standard tab (assign/picker mode): Advanced Selection filter (shown on
          arrival, same as the lesson flow) + always-visible filtered question
          carousel, standard-scoped. */}
      {showTabs && activeTab === "core" && (
        <Card className="detail-section">
          <Card.Body style={{ padding: "24px", backgroundColor: colors.milkGlass500 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, marginBottom: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0, color: "#002E19", fontSize: 20, fontWeight: 700, lineHeight: "28px" }}>
                  Preview Question bank
                </h3>
                <p style={{ margin: 0, color: "#002E19", fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 400, lineHeight: "22px", letterSpacing: "0.25px" }}>
                  Explore the available questions for the core standard using the filters below.
                  After you select &quot;Create Groups&quot; you can customize the question set for
                  each group or let OKO randomly assign questions from this bank.
                </p>
              </div>
            </div>
            <QuestionFilterPanel
              filter={coreFilter}
              onChange={(next) => setFilter("core", null, next)}
              facets={coreFacets}
              hasLoaded={coreFacetsLoaded}
              error={coreFacetsError}
              showStandards={false}
              showAutoAssign={false}
            />
            {coreQuestionsLoading ? (
              <div style={{ textAlign: "center", padding: "32px" }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
            ) : coreQuestions.length > 0 ? (
              <ExampleQuestionsCarousel questions={coreQuestions} total={coreQuestionsTotal} onLoadMore={loadMoreCoreQuestions} />
            ) : (
              <div style={{ color: colors.milkGlass950, padding: "16px 0" }}>
                {zeroCountWarning || "No questions available for this selection."}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* FE-3 Prerequisites tab: level-1 prereqs from BE-1. */}
      {showTabs && activeTab === "prerequisites" && (
        <div className="tab-panel">
          {isLoadingRelationships ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p style={{ marginTop: "16px" }}>Loading prerequisites...</p>
            </div>
          ) : (
            renderPrerequisiteSections(relationships?.prerequisites)
          )}
        </div>
      )}

      {/* FE-3 Extensions tab: level-1 extensions from BE-1. */}
      {showTabs && activeTab === "extensions" && (
        <div className="tab-panel">
          {isLoadingRelationships ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p style={{ marginTop: "16px" }}>Loading extensions...</p>
            </div>
          ) : (
            renderLevelGroups(
              relationships?.extensions,
              <div className="relationships-header">
                <div className="relationships-header-title">Extensions for {getEntityCode()}</div>
                <div className="relationships-header-subtitle">
                  The standards that build directly upon the Core Standard.
                </div>
              </div>,
              "extensions"
            )
          )}
        </div>
      )}

      {/* Legacy prerequisites section — only shown outside assign-mode. */}
      {!showTabs && isStandard && prerequisites.length > 0 && (
        <Card className="detail-section">
          <Card.Header
            className="section-header"
            role="button"
            tabIndex={0}
            aria-expanded={showPrerequisites}
            onClick={() => setShowPrerequisites(!showPrerequisites)}
            onKeyDown={onActivateKeyDown(() => setShowPrerequisites(!showPrerequisites))}
            style={{ cursor: "pointer" }}
          >
            <div className="section-header-content">
              <img src={showPrerequisites ? arrow_down : arrow_right} alt="arrow" style={{ width: "16px" }} />
              <span className="section-title">Prerequisites for {getEntityCode()}</span>
            </div>
          </Card.Header>

          {showPrerequisites && (
            <Card.Body style={{ padding: 0 }}>
              {prerequisites.map((prereq, index) => (
                <div key={prereq.code || index} className="prerequisite-item">
                  <div
                    className="prerequisite-header"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open standard ${prereq.code}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onStandardClick && onStandardClick(prereq, standard.grade, standard.domain, standard.cluster, standard)}
                    onKeyDown={onActivateKeyDown(() => onStandardClick && onStandardClick(prereq, standard.grade, standard.domain, standard.cluster, standard))}
                  >
                    <span className="prerequisite-code">STANDARD - {prereq.code}</span>
                  </div>
                  <h6
                    className="prerequisite-title"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onStandardClick && onStandardClick(prereq, standard.grade, standard.domain, standard.cluster, standard)}
                  >
                    {prereq.name || "Loading..."}
                  </h6>
                  {prereq.description && (
                    <p className="prerequisite-description">{prereq.description}</p>
                  )}
                  <button
                    className="view-questions-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openQuestionsModal(prereq);
                    }}
                  >
                    👁 View Questions
                  </button>
                </div>
              ))}
            </Card.Body>
          )}
        </Card>
      )}

      {/* Action Buttons — hidden in pickerMode; the View & Change Standard
          page owns its own footer. */}
      {!pickerMode && isAssignMode && (
      <div className="action-buttons-new">
        <div className="buttons-container">
          <button
            className="cancel-button-new"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Back to Groups
          </button>
          <button
            className="save-button-new"
            onClick={() => setIsDistributionModalOpen(true)}
            disabled={!canAssign}
            title={!canAssign ? "Select a standard, cluster, or domain (not a whole grade)" : ""}
          >
            Create Groups
          </button>
        </div>
      </div>
      )}

      {/* Non-assign-mode footer — For Group / Cancel / Save, wired to
          handleUpdateGroupCode which persists the selected standard to the group.
          Only shown when a real group code is attached and its config has loaded,
          so the Save action has a valid group + classroom_id to post to. */}
      {!pickerMode && !isAssignMode && hasAttachedGroupCode && groupConfig && (
      <div className="action-buttons-new">
        <div className="group-code-label">
          For Group: {groupCode.toUpperCase()}
        </div>
        <div className="buttons-container">
          <button
            className="cancel-button-new"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="save-button-new"
            onClick={handleUpdateGroupCode}
            disabled={isSaving || isGrade}
            title={isGrade ? "Please select a specific domain within the grade" : ""}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      )}

      {/* FE-4 distribution modal — only reachable when navigated from FE-2 with intent=assign */}
      {isAssignMode && (
        <AssignGroupsDistributionModal
          isOpen={isDistributionModalOpen}
          onClose={() => setIsDistributionModalOpen(false)}
          classroomId={classroomIdFromUrl}
          standardCode={canAssign ? standard.code : null}
          standardName={canAssign ? standard.name : null}
          totalAvailable={totalAvailableFromUrl || 10}
          // Cluster targets are Core-only by design — a cluster has no prereq /
          // extension relationships to distribute groups across.
          hasPrerequisites={isStandard && hasPrerequisites}
          hasExtensions={isStandard && hasExtensions}
          questionFilters={toTrackQuestionFilters(filters)}
          customizedTracks={Array.from(customizedTrackTypes(filters))}
          onSaved={() => {
            setIsDistributionModalOpen(false);
            handleCancel();
          }}
        />
      )}

      {/* FE-8: single shared modal for prereq / extension "View Questions" buttons. */}
      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => setViewQuestionsTarget(null)}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
      />

      {/* Error Message */}
      {error && (
        <Alert
          style={{ marginTop: '16px' }}
          variant="danger"
          dismissible
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </div>
  );
};

